/**
 * Gradient course progress — the shape stored locally and in Mongo, plus the
 * merge that reconciles two copies of it.
 *
 * Every field is a conflict-free type, so any two devices can edit offline and
 * merge in either order and land on the same result:
 *   - steps   → max of the furthest step reached (grow-only)
 *   - done    → earliest completion stamp (grow-only set)
 *   - solved  → earliest solve stamp (grow-only set)
 *   - code    → last-writer-wins by timestamp
 *   - days    → union of active days (grow-only set)
 * `resetAt` is itself last-writer-wins: entries stamped before it are dropped,
 * which is how "start fresh" propagates without breaking the grow-only rules.
 *
 * Shared by the browser and the API route, so it must stay free of Node and
 * DOM APIs.
 */

export type Stamp = number

export type ProgressState = {
  v: 1
  resetAt: Stamp
  steps: Record<string, { i: number; t: Stamp }>
  done: Record<string, Stamp>
  solved: Record<string, Stamp>
  code: Record<string, { s: string; t: Stamp }>
  days: Record<string, Stamp>
  updatedAt: Stamp
}

export const EMPTY_PROGRESS: ProgressState = Object.freeze({
  v: 1,
  resetAt: 0,
  steps: {},
  done: {},
  solved: {},
  code: {},
  days: {},
  updatedAt: 0,
}) as ProgressState

export function emptyProgress(): ProgressState {
  return { v: 1, resetAt: 0, steps: {}, done: {}, solved: {}, code: {}, days: {}, updatedAt: 0 }
}

// ─── Limits (enforced on both sides; the server trusts nothing) ──────────────

export const LIMITS = {
  keyLength: 96,
  codeChars: 40_000,
  codeEntries: 400,
  stepEntries: 400,
  solvedEntries: 2_000,
  dayEntries: 800,
  payloadBytes: 1_200_000,
} as const

const KEY_RE = /^[a-z0-9][a-z0-9:/._-]*$/i
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/

function okKey(key: string): boolean {
  return key.length > 0 && key.length <= LIMITS.keyLength && KEY_RE.test(key)
}

function okStamp(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value < 8.64e15
}

function take<T>(entries: [string, T][], max: number, newest: (value: T) => number): [string, T][] {
  if (entries.length <= max) return entries
  return entries.sort((a, b) => newest(b[1]) - newest(a[1])).slice(0, max)
}

/** Coerce untrusted input into a valid ProgressState, dropping anything malformed. */
export function sanitizeProgress(input: unknown): ProgressState {
  const out = emptyProgress()
  if (!input || typeof input !== 'object') return out
  const raw = input as Record<string, unknown>

  if (okStamp(raw.resetAt)) out.resetAt = raw.resetAt
  if (okStamp(raw.updatedAt)) out.updatedAt = raw.updatedAt

  const record = (value: unknown): [string, unknown][] =>
    value && typeof value === 'object' && !Array.isArray(value)
      ? Object.entries(value as Record<string, unknown>).filter(([k]) => okKey(k))
      : []

  const steps: [string, { i: number; t: Stamp }][] = []
  for (const [k, v] of record(raw.steps)) {
    const step = v as { i?: unknown; t?: unknown } | null
    if (step && Number.isInteger(step.i) && (step.i as number) >= 0 && (step.i as number) < 500 && okStamp(step.t)) {
      steps.push([k, { i: step.i as number, t: step.t }])
    }
  }
  out.steps = Object.fromEntries(take(steps, LIMITS.stepEntries, s => s.t))

  const stamps = (value: unknown, max: number) =>
    Object.fromEntries(take(record(value).filter((e): e is [string, number] => okStamp(e[1])), max, t => t))
  out.done = stamps(raw.done, LIMITS.stepEntries)
  out.solved = stamps(raw.solved, LIMITS.solvedEntries)
  out.days = Object.fromEntries(
    take(
      Object.entries(stamps(raw.days, LIMITS.dayEntries * 2)).filter(([k]) => DAY_RE.test(k)),
      LIMITS.dayEntries,
      t => t,
    ),
  )

  const code: [string, { s: string; t: Stamp }][] = []
  for (const [k, v] of record(raw.code)) {
    const entry = v as { s?: unknown; t?: unknown } | null
    if (entry && typeof entry.s === 'string' && entry.s.length <= LIMITS.codeChars && okStamp(entry.t)) {
      code.push([k, { s: entry.s, t: entry.t }])
    }
  }
  out.code = Object.fromEntries(take(code, LIMITS.codeEntries, c => c.t))

  return out
}

// ─── Merge ─────────────────────────────────────────────────────────────────────

function mergeStamps(a: Record<string, Stamp>, b: Record<string, Stamp>, floor: Stamp): Record<string, Stamp> {
  const out: Record<string, Stamp> = {}
  for (const src of [a, b]) {
    for (const [k, t] of Object.entries(src)) {
      if (t < floor) continue
      out[k] = k in out ? Math.min(out[k], t) : t
    }
  }
  return out
}

/** Commutative, associative, idempotent merge of two progress copies. */
export function mergeProgress(a: ProgressState, b: ProgressState): ProgressState {
  const resetAt = Math.max(a.resetAt, b.resetAt)

  const steps: ProgressState['steps'] = {}
  for (const src of [a.steps, b.steps]) {
    for (const [k, s] of Object.entries(src)) {
      if (s.t < resetAt) continue
      const cur = steps[k]
      steps[k] = cur ? { i: Math.max(cur.i, s.i), t: Math.max(cur.t, s.t) } : { ...s }
    }
  }

  const code: ProgressState['code'] = {}
  for (const src of [a.code, b.code]) {
    for (const [k, c] of Object.entries(src)) {
      if (c.t < resetAt) continue
      const cur = code[k]
      // Tie-break on content so both sides pick the same winner.
      if (!cur || c.t > cur.t || (c.t === cur.t && c.s > cur.s)) code[k] = { ...c }
    }
  }

  return {
    v: 1,
    resetAt,
    steps,
    done: mergeStamps(a.done, b.done, resetAt),
    solved: mergeStamps(a.solved, b.solved, resetAt),
    code,
    days: mergeStamps(a.days, b.days, resetAt),
    updatedAt: Math.max(a.updatedAt, b.updatedAt),
  }
}

/** True when the state holds anything worth backing up remotely. */
export function hasMeaningfulProgress(state: ProgressState): boolean {
  return (
    Object.keys(state.done).length > 0 ||
    Object.keys(state.solved).length > 0 ||
    Object.keys(state.code).length > 0 ||
    Object.values(state.steps).some(s => s.i > 0)
  )
}

/** Structural equality, used to skip redundant writes and re-renders. */
export function sameProgress(a: ProgressState, b: ProgressState): boolean {
  return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b))
}

function canonical(state: ProgressState) {
  const sort = <T,>(r: Record<string, T>) => Object.fromEntries(Object.entries(r).sort(([x], [y]) => (x < y ? -1 : 1)))
  return {
    resetAt: state.resetAt,
    steps: sort(state.steps),
    done: sort(state.done),
    solved: sort(state.solved),
    code: sort(state.code),
    days: sort(state.days),
  }
}

// ─── Sync keys ─────────────────────────────────────────────────────────────────
// 20 Crockford base32 characters = 100 bits. Short enough to type between
// devices, far too large to guess (and the API rate-limits misses anyway).

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'
export const SYNC_KEY_LENGTH = 20

export function generateSyncKey(randomBytes: (n: number) => Uint8Array): string {
  const bytes = randomBytes(SYNC_KEY_LENGTH)
  let key = ''
  for (let i = 0; i < SYNC_KEY_LENGTH; i++) key += CROCKFORD[bytes[i] & 31]
  return key
}

/** Accepts sloppy human input (lowercase, dashes, O/I/L lookalikes). */
export function normalizeSyncKey(input: string): string | null {
  const cleaned = input
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1')
  if (cleaned.length !== SYNC_KEY_LENGTH) return null
  for (const ch of cleaned) if (!CROCKFORD.includes(ch)) return null
  return cleaned
}

export function formatSyncKey(key: string): string {
  return key.match(/.{1,5}/g)?.join('-') ?? key
}

// ─── Derived stats ─────────────────────────────────────────────────────────────

export function dayKey(date = new Date()): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Consecutive active days ending today (or yesterday, so a streak survives until midnight). */
export function currentStreak(state: ProgressState, now = new Date()): number {
  const days = state.days
  const cursor = new Date(now)
  if (!(dayKey(cursor) in days)) cursor.setDate(cursor.getDate() - 1)
  let streak = 0
  while (dayKey(cursor) in days) {
    streak++
    cursor.setDate(cursor.getDate() - 1)
  }
  return streak
}

/**
 * The course's running joke: your "training loss". Random guessing over ten
 * classes scores ln(10) ≈ 2.303 in cross-entropy — that's where everyone
 * starts — and every finished lesson pushes it toward zero.
 */
export function learnerLoss(completed: number, total: number): number {
  const p = total === 0 ? 0 : Math.min(1, completed / total)
  return Math.log(10) * Math.pow(1 - p, 1.6)
}
